import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
  isSessionSuperAdmin,
  getSessionRoleName,
} from "../../../utils/permissionUtils";
import { organizationHasParticipantManagerModule } from "../../../utils/orgModuleAccess";
import { PERMISSIONS } from "../../../constants/permissions";

export { organizationHasParticipantManagerModule };

/** Assignable Participant Manager keys (Role / User permission cards). */
export const PM_PERMISSION_KEYS = {
  LIST: "PARTICIPANT_MANAGER",
  CAMPAIGN_TEMPLATES: "CAMPAIGN_TEMPLATES",
  FEASIBILITY: "FEASIBILITY_MANAGER",
  BULK_COMM: "SEND_BULK_EMAIL_SMS",
};

/** Org must be licensed for Participant Manager (role alone is not enough). */
export function canAccessParticipantManager({ userData }) {
  return organizationHasParticipantManagerModule(userData);
}

/**
 * Shared CAPA/E-Planner-style access for Participant Manager.
 * System Administrator / Super Admin → all keys (via getAllPermissionKeys).
 */
export function useParticipantAccess() {
  const { userData, loading } = useUserData();
  const sessionRole = getSessionRoleName(userData);
  const isSystemAdmin =
    isSystemAdministratorRole(sessionRole) || isSessionSuperAdmin(userData);

  const permissions = useMemo(
    () =>
      isSystemAdmin
        ? getAllPermissionKeys()
        : parsePermissions(userData?.Permission || ""),
    [isSystemAdmin, userData?.Permission],
  );

  const moduleAccess = canAccessParticipantManager({ userData });

  const can = (key) => {
    if (!moduleAccess) return false;
    if (isSystemAdmin) return true;
    return hasPermission(permissions, key);
  };

  const canViewList = can(PM_PERMISSION_KEYS.LIST);
  const canViewCampaignTemplates = can(PM_PERMISSION_KEYS.CAMPAIGN_TEMPLATES);
  const canViewFeasibility = can(PM_PERMISSION_KEYS.FEASIBILITY);
  const canSendBulkComm = can(PM_PERMISSION_KEYS.BULK_COMM);
  /** Payment email template — System Administrator only (not assignable PM permission). */
  const canViewPaymentEmailTemplate =
    moduleAccess && isSystemAdministratorRole(sessionRole);

  return {
    userData,
    loading,
    permissions,
    isSystemAdmin,
    canAccessParticipantManager: moduleAccess,
    /** List + profile + add — one sidebar / role permission. */
    canViewActiveParticipants: canViewList,
    canViewInactiveParticipants: canViewList,
    canAddParticipant: canViewList,
    canViewParticipantProfile: canViewList,
    canViewCampaignTemplates,
    canViewFeasibility,
    canSendBulkComm,
    canViewPaymentEmailTemplate,
    participantManagerPermissions: PERMISSIONS.PARTICIPANT_MANAGER,
  };
}

export function canAccessParticipantManagerScreen(userData) {
  return canAccessParticipantManager({ userData });
}

/**
 * Wait for session before denying access (avoids redirect while userData is still null).
 * Requires org Participant Manager licence (System Administrator included).
 */
export function ParticipantAccessGate({
  allow,
  loading,
  children,
  redirectTo = "/notification-center",
}) {
  if (loading) {
    return <div className="participant-manager-page pm-page--bootstrapping" />;
  }
  if (!allow) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}
